/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { AnimatedWrapper } from '@atlassian/ai-common';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerLoadingDialogShown } from '../../../../ai-answer-dialog/on-ai-answer-loading-dialog-shown';
import type { commonAttributesType, SAINResearchStep } from '../../../../ai-answer-dialog/types';
import { useAnalytics } from '../../../../common/controller/analytics/use-analytics';
import { onAIAnswerMinimizedLoadingCollapseClicked } from '../analytics/on-ai-answer-minimized-loading-collapse-clicked';
import { onAIAnswerMinimizedLoadingExpandClicked } from '../analytics/on-ai-answer-minimized-loading-expand-clicked';

import { MorphingShapeLoader } from './morphing-shape-loader';
import { ResearchStepRow } from './research-step-row';
import { ShimmerText } from './shimmer-text';

/**
 * Pulls renderable research steps from the shared map, sorted oldest → newest.
 * Mirrors Chat: a step becomes visible as soon as its `RESEARCH_STEP_TITLE` is
 * received. The step's `completed` flag still controls visual styling (shimmer
 * while incomplete) but does not gate rendering, so users see step text
 * immediately rather than only briefly after `RESEARCH_STEP_COMPLETE`.
 */
const getRenderableSteps = (steps: { [stepId: string]: SAINResearchStep } | undefined) => {
	if (!steps) {
		return [];
	}
	return Object.values(steps)
		.filter((s) => !!s.title)
		.sort((a, b) => a.order - b.order);
};

const styles = cssMap({
	container: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		height: '64px',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface.sunken'),
		// Match AnimatedWrapper's border radius so the background doesn't clip the animation corners
		borderRadius: token('radius.small'),
	},
	shimmerWrapper: {
		flexGrow: 1,
		minWidth: '0px',
	},
	expandedContent: {
		paddingTop: token('space.0'),
		paddingBottom: token('space.150'),
		// Match the loader row's left padding so research steps left-align with
		// the MorphingShapeLoader above.
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	expandedWrapper: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
	},
	/**
	 * Loader row when expanded with steps — a flex row with stretched icon column
	 * so MorphingShapeLoader sits at top and the connector rule grows downward.
	 * Mirrors Chat's `substepRow`.
	 */
	/**
	 * Invisible placeholder that reserves the same space as the expand button
	 * before research steps arrive. aria-hidden on the element itself removes it
	 * from the accessibility tree — unlike visibility:hidden which keeps it focusable.
	 */
	buttonPlaceholder: {
		opacity: 0,
		pointerEvents: 'none',
		flexShrink: 0,
	},
	expandedLoaderRow: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		columnGap: token('space.100'),
		alignItems: 'stretch',
		paddingTop: token('space.100'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		boxSizing: 'border-box',
		height: '64px',
	},
	/**
	 * Two-slot icon column: an empty top slot (flex-grow:1) above the loader
	 * and a connector slot below it. Centers the MorphingShapeLoader vertically
	 * in its row, exactly like `ResearchStepRow`'s icon column.
	 */
	loaderIconColumn: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: '24px',
		flexShrink: 0,
		alignSelf: 'stretch',
	},
	/**
	 * The top slot above the MorphingShapeLoader grows to vertically center the
	 * loader within its row — matching the centered text + button to its right.
	 */
	loaderRuleSlotTop: {
		flexGrow: 1,
		width: '0px',
	},
	loaderRuleSlotWithLine: {
		flexGrow: 1,
		width: '0px',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
	},
	/**
	 * Loader row main content (text + button) — uses flex-row with center
	 * alignment to vertically center everything in the row.
	 */
	loaderContent: {
		flexGrow: 1,
		minWidth: '0px',
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		columnGap: token('space.100'),
	},
});

const messages = defineMessages({
	thinking_deeper: {
		id: 'search-ai.smart-answer-card-minimized-loading.thinking-deeper',
		defaultMessage: 'Thinking deeper and analyzing your question...',
		description: 'Loading text shown while the AI is generating a deeper answer',
	},
	expand_answer: {
		id: 'search-ai.smart-answer-card-minimized-loading.expand-answer',
		defaultMessage: 'Expand answer',
		description: 'Button label to expand the smart answer card while it is loading',
	},
	collapse_answer: {
		id: 'search-ai.smart-answer-card-minimized-loading.collapse-answer',
		defaultMessage: 'Hide answer',
		description: 'Button label to hide/collapse the smart answer card while it is loading',
	},
});

type SmartAnswerCardMinimizedLoadingProps = {
	onExpandClick?: () => void;
	/**
	 * Tracks whether the user has clicked the "Expand answer" button during loading.
	 * - false (default): show the "Expand answer" button (user has not requested expansion)
	 * - true: show the "Hide answer" button and render the expanded preview content
	 *   (user has requested expansion during loading)
	 */
	userExpandedDuringLoading?: boolean;
	commonAttributes: commonAttributesType;
	testId?: string;
};

/** Max number of completed research steps to show beneath the loader in the expanded view. */
const MAX_EXPANDED_STEPS = 2;

export function SmartAnswerCardMinimizedLoading({
	onExpandClick,
	userExpandedDuringLoading = false,
	commonAttributes,
	testId,
}: SmartAnswerCardMinimizedLoadingProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { researchSteps } = useAIAnswerContext();

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleExpandClick = () => {
		fireAnalyticsEvent(onAIAnswerMinimizedLoadingExpandClicked(commonAttributes));
		onExpandClick?.();
	};

	const handleCollapseClick = () => {
		fireAnalyticsEvent(onAIAnswerMinimizedLoadingCollapseClicked(commonAttributes));
		onExpandClick?.();
	};

	const renderableSteps = getRenderableSteps(researchSteps);
	// Expanded view: take the last MAX_EXPANDED_STEPS renderable steps in
	// oldest-first order so the most recent step appears at the bottom.
	const recentStepsForExpanded = renderableSteps.slice(-MAX_EXPANDED_STEPS);

	// The header row contains the MorphingShapeLoader, the shimmer/step text,
	// and the expand/collapse toggle button. This row is rendered in both
	// collapsed and expanded states.
	const headerRow = (
		<Inline
			alignBlock="center"
			space="space.100"
			xcss={styles.container}
			testId={testId}
			grow="fill"
		>
			<MorphingShapeLoader />
			<Inline alignBlock="center" grow="fill" xcss={styles.shimmerWrapper}>
				<ShimmerText size="medium" truncate>
					{formatMessage(messages.thinking_deeper)}
				</ShimmerText>
			</Inline>
			{/* Render the button when steps exist or user has expanded.
			    When no steps yet, render an aria-hidden placeholder of the same
			    size to reserve space and prevent layout shift — this removes the
			    ghost button from the accessibility tree entirely. */}
			{!userExpandedDuringLoading && renderableSteps.length === 0 ? (
				<Box xcss={styles.buttonPlaceholder} aria-hidden="true">
					<Button
						spacing="compact"
						tabIndex={-1}
						iconAfter={(iconProps) => <EyeOpenIcon {...iconProps} size="small" />}
					>
						<Text color="color.text.subtlest">{formatMessage(messages.expand_answer)}</Text>
					</Button>
				</Box>
			) : userExpandedDuringLoading ? (
				<Button
					spacing="compact"
					onClick={handleCollapseClick}
					iconAfter={(iconProps) => <EyeOpenStrikethroughIcon {...iconProps} size="small" />}
				>
					<Text color="color.text.subtlest">{formatMessage(messages.collapse_answer)}</Text>
				</Button>
			) : (
				<Button
					spacing="compact"
					onClick={handleExpandClick}
					iconAfter={(iconProps) => <EyeOpenIcon {...iconProps} size="small" />}
				>
					<Text color="color.text.subtlest">{formatMessage(messages.expand_answer)}</Text>
				</Button>
			)}
		</Inline>
	);

	return (
		<AnimatedWrapper>
			{userExpandedDuringLoading ? (
				<Box xcss={styles.expandedWrapper}>
					{/*
					 * Expanded loader row: uses a flex-column icon column so the
					 * MorphingShapeLoader sits at the top and the `loaderVerticalRule`
					 * grows to fill the space below — connecting tip-to-tip with the
					 * first research step's icon. Mirrors Chat's icon column pattern.
					 * Only renders the connector when there are steps to connect to.
					 */}
					{recentStepsForExpanded.length > 0 ? (
						<Box xcss={styles.expandedLoaderRow} testId={testId}>
							<Box xcss={styles.loaderIconColumn}>
								<Box xcss={styles.loaderRuleSlotTop} />
								<MorphingShapeLoader />
								<Box xcss={styles.loaderRuleSlotWithLine} />
							</Box>
							<Box xcss={styles.loaderContent}>
								<Box xcss={styles.shimmerWrapper}>
									<ShimmerText size="medium" truncate>
										{formatMessage(messages.thinking_deeper)}
									</ShimmerText>
								</Box>
								<Button
									spacing="compact"
									onClick={handleCollapseClick}
									iconAfter={(iconProps) => (
										<EyeOpenStrikethroughIcon {...iconProps} size="small" />
									)}
								>
									<Text color="color.text.subtlest">{formatMessage(messages.collapse_answer)}</Text>
								</Button>
							</Box>
						</Box>
					) : (
						<Inline
							alignBlock="center"
							space="space.100"
							xcss={styles.container}
							testId={testId}
							grow="fill"
						>
							<MorphingShapeLoader />
							<Inline alignBlock="center" grow="fill" xcss={styles.shimmerWrapper}>
								<ShimmerText size="medium" truncate>
									{formatMessage(messages.thinking_deeper)}
								</ShimmerText>
							</Inline>
							<Button
								spacing="compact"
								onClick={handleCollapseClick}
								iconAfter={(iconProps) => <EyeOpenStrikethroughIcon {...iconProps} size="small" />}
							>
								<Text color="color.text.subtlest">{formatMessage(messages.collapse_answer)}</Text>
							</Button>
						</Inline>
					)}
					{recentStepsForExpanded.length > 0 && (
						<Box xcss={styles.expandedContent}>
							{recentStepsForExpanded.map((step, index) => (
								<ResearchStepRow
									key={step.stepId}
									step={step}
									// Top connector on every step (connects up to previous row /
									// loader); bottom connector on all but the last step.
									showTopConnector
									showBottomConnector={index < recentStepsForExpanded.length - 1}
									testId={testId ? `${testId}-step-${step.stepId}` : undefined}
								/>
							))}
						</Box>
					)}
				</Box>
			) : (
				headerRow
			)}
		</AnimatedWrapper>
	);
}
